import React, { useState, useEffect, useMemo, useRef } from "react";
import { useMantineColorScheme } from "@mantine/styles";
import { ReactComponent as GridIcon } from "../../assets/segment_energy_infrastructure.svg";
import { ReactComponent as BatteryIcon } from "../../assets/battery2_general.svg";
import { ReactComponent as LoadIcon } from "../../assets/segment_building.svg";
import { ReactComponent as SiteIcon } from "../../assets/site.svg";
import { ReactComponent as GensetIcon } from "../../assets/diesel_generator.svg";
import { ReactComponent as PvIcon } from "../../assets/solar_panel1.svg";
import { ReactComponent as EnergyIcon } from "../../assets/segment_electrical_energy.svg";

const defaultRawData = {
  fromPV: {
    toBuilding: 339053.2787,
    toEVCS: "NaN",
    toBattery: 5688.6362,
    toGrid: 323676.6574,
    toExcessEnergy: 0,
  },
  fromBattery: {
    toBuilding: 155842.3509,
    toEVCS: "NaN",
    toGrid: 16468.4906,
    toExcessEnergy: 0,
  },
  fromGenset: {
    toBuilding: 1142345.3468,
    toEVCS: "NaN",
    toBattery: 74972.6239,
    toGrid: 394522.0293,
    toExcessEnergy: 0,
  },
  fromGrid: {
    toBuilding: 1873436.3628,
    toEVCS: 0,
    toBattery: 97977.3798,
  },
  fromUnmetEnergy: {
    toBuilding: 0,
    toEVCS: "NaN",
    toBattery: 0,
  },
};

const baseNodesConfig = {
  Grid: { color: "#3f639b", label: "Buying" },
  GridSell: { color: "#3b85d9", label: "Selling" },
  PV: { color: "#339f40", label: "PV (Solar)" },
  Battery: { color: "#5cd165", label: "BESS" },
  Genset: { color: "#228532", label: "Genset" },
  Building: { color: "#2a2a2a", label: "Load" },
  EVCS: { color: "#9a60b4", label: "EV Chargers" },
  ExcessEnergy: { color: "#888888", label: "Excess Energy" },
};

const CANVAS_WIDTH = 1100;
const CANVAS_HEIGHT = 750;
const NODE_HEIGHT = 20;
const LEFT_PADDING = 30;
const RIGHT_PADDING = 30;
const ICON_SIZE = 30;
const ICON_RING_RADIUS = 20;
const ICON_HIT_RADIUS = 24;
const GRID_ICON_CENTER_OFFSET_X = 30;
const BUILDING_VALUE_FONT_SIZE = 18;
const BUILDING_LABEL_FONT_SIZE = 20;
const BUILDING_PANEL_TOP_PADDING = 8;
const BUILDING_TEXT_GAP = 8;
const BUILDING_PANEL_BOTTOM_PADDING = 8;
const BUILDING_PANEL_HEIGHT =
  BUILDING_PANEL_TOP_PADDING +
  BUILDING_VALUE_FONT_SIZE +
  BUILDING_TEXT_GAP +
  BUILDING_LABEL_FONT_SIZE +
  BUILDING_PANEL_BOTTOM_PADDING;
const BUILDING_ICON_SIZE = Math.max(16, BUILDING_PANEL_HEIGHT - 4);
const BUILDING_ICON_TEXT_GAP = 10;

const NODE_ICON_MAP = {
  Grid: GridIcon,
  GridSell: GridIcon,
  PV: PvIcon,
  Battery: BatteryIcon,
  Genset: GensetIcon,
  Building: LoadIcon,
  EVCS: SiteIcon,
  ExcessEnergy: EnergyIcon,
};

const isValidObject = (value) => value && typeof value === "object" && !Array.isArray(value);

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const normalized = String(value).replace(/,/g, "").trim();
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function MicrogridInteractiveSankey({ energyFlow }) {
  const { colorScheme } = useMantineColorScheme();
  const [isReady, setIsReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [baseNodes, setBaseNodes] = useState({});
  const [baseLinks, setBaseLinks] = useState([]);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [hoveredLinkId, setHoveredLinkId] = useState(null);

  const [nodePositions, setNodePositions] = useState({});
  const [dragInfo, setDragInfo] = useState(null);

  const tooltipRef = useRef(null);
  const tooltipTitleRef = useRef(null);
  const tooltipValueRef = useRef(null);
  const tooltipDetailsRef = useRef(null);
  const initialNodePositionsRef = useRef({});

  const svgRef = useRef(null);

  const isDarkMode = colorScheme === "dark";
  const pageBg = isDarkMode ? "#111" : "#ffffff";
  const baseTextColor = isDarkMode ? "#ffffff" : "#24313d";
  const titleColor = isDarkMode ? "#a0a0a0" : "#4f5b66";
  const cardBg = isDarkMode ? "#141414" : "rgba(255,255,255,0.66)";
  const panelFill = isDarkMode ? "#1d1d1d" : "rgba(255,255,255,0.72)";
  const panelSubText = isDarkMode ? "#888" : "#5f6670";
  const buildingPanelFill = isDarkMode ? "#1d1d1d" : "rgba(255,255,255,0.72)";
  const buildingTotalFill = isDarkMode ? "#2f2214" : "rgba(255,244,224,0.74)";

  const flowInput = useMemo(() => {
    if (isValidObject(energyFlow) && Object.keys(energyFlow).length > 0) return energyFlow;
    return defaultRawData;
  }, [energyFlow]);

  useEffect(() => {
    let tempLinks = [];
    const tempNodes = JSON.parse(JSON.stringify(baseNodesConfig));

    Object.keys(tempNodes).forEach((k) => {
      tempNodes[k].inValue = 0;
      tempNodes[k].outValue = 0;
      tempNodes[k].id = k;
      tempNodes[k].widthScale = 1.0;
    });

    for (const source in flowInput) {
      if (!Object.prototype.hasOwnProperty.call(flowInput, source)) continue;
      const sourceObj = flowInput[source];
      if (!isValidObject(sourceObj)) continue;

      const sKey = String(source).replace("from", "");
      if (!tempNodes[sKey]) continue;

      for (const target in sourceObj) {
        if (!Object.prototype.hasOwnProperty.call(sourceObj, target)) continue;
        if (typeof target !== "string" || !target.startsWith("to")) continue;

        let tKey = target.replace("to", "");
        if (tKey === "Grid") tKey = "GridSell";
        if (!tempNodes[tKey]) continue;

        const val = toNumber(sourceObj[target]);
        if (val > 0) {
          const mwhValue = val / 1000;
          tempLinks.push({ source: sKey, target: tKey, value: mwhValue });
        }
      }
    }

    tempLinks.forEach((l) => {
      tempNodes[l.source].outValue += l.value;
      tempNodes[l.target].inValue += l.value;
    });

    Object.keys(tempNodes).forEach((k) => {
      tempNodes[k].value = Math.max(tempNodes[k].inValue, tempNodes[k].outValue);
    });

    const maxTierValue = Math.max(
      (tempNodes.Grid?.value || 0) + (tempNodes.GridSell?.value || 0),
      (tempNodes.PV?.value || 0) + (tempNodes.Genset?.value || 0),
      tempNodes.Battery?.value || 0,
      (tempNodes.Building?.value || 0) +
        (tempNodes.EVCS?.value || 0) +
        (tempNodes.ExcessEnergy?.value || 0)
    );

    const calculatedScale = maxTierValue > 0 ? (CANVAS_WIDTH - 300) / maxTierValue : 1;
    Object.keys(tempNodes).forEach((id) => {
      tempNodes[id].width = tempNodes[id].value * calculatedScale * tempNodes[id].widthScale;
    });

    const Y_TOP = 140;
    const Y_70_FROM_BOT = CANVAS_HEIGHT * 0.3 + 20;
    const Y_40_FROM_BOT = CANVAS_HEIGHT * 0.6;
    const Y_BOTTOM = 560;

    const initialPositions = {};

    if (tempNodes.Grid?.value > 0) {
      initialPositions.Grid = { x: LEFT_PADDING, y: Y_TOP };
    }

    const gridEnd = LEFT_PADDING + (tempNodes.Grid?.width || 0);
    const secondaryStartX = gridEnd + 80;

    if (tempNodes.Battery?.value > 0) {
      initialPositions.Battery = { x: secondaryStartX, y: Y_40_FROM_BOT };
    }

    let pvStartX = secondaryStartX;
    if (tempNodes.Battery?.value > 0) {
      pvStartX = initialPositions.Battery.x + tempNodes.Battery.width + 40;
    } else {
      pvStartX += 40;
    }

    let currentX = pvStartX;
    if (tempNodes.PV?.value > 0) {
      initialPositions.PV = { x: currentX, y: Y_70_FROM_BOT };
      currentX += tempNodes.PV.width + 5;
    }
    if (tempNodes.Genset?.value > 0) {
      initialPositions.Genset = { x: currentX, y: Y_70_FROM_BOT };
      currentX += tempNodes.Genset.width + 50;
    }

    if (tempNodes.GridSell?.value > 0) {
      const sellWidth = tempNodes.GridSell.width || 0;
      const hasPV = tempNodes.PV?.value > 0 && initialPositions.PV;
      const hasGenset = tempNodes.Genset?.value > 0 && initialPositions.Genset;

      let sellX = secondaryStartX;
      if (hasPV && hasGenset) {
        const pvCenter = initialPositions.PV.x + (tempNodes.PV.width || 0) / 2;
        const gensetCenter = initialPositions.Genset.x + (tempNodes.Genset.width || 0) / 2;
        sellX = (pvCenter + gensetCenter) / 2 - sellWidth / 2;
      } else if (hasPV) {
        const pvCenter = initialPositions.PV.x + (tempNodes.PV.width || 0) / 2;
        sellX = pvCenter - sellWidth / 2;
      } else if (hasGenset) {
        const gensetCenter = initialPositions.Genset.x + (tempNodes.Genset.width || 0) / 2;
        sellX = gensetCenter - sellWidth / 2;
      } else {
        const maxContentX = Math.max(currentX, secondaryStartX + (tempNodes.Battery?.width || 0));
        sellX = maxContentX + 80;
      }

      sellX = Math.max(sellX, gridEnd + 80);
      initialPositions.GridSell = { x: sellX, y: Y_TOP };
    }

    if (tempNodes.Building?.value > 0) {
      initialPositions.Building = {
        x: CANVAS_WIDTH / 2 - tempNodes.Building.width / 2 - 40,
        y: Y_BOTTOM,
      };
    }

    const buildingEnd = initialPositions.Building
      ? initialPositions.Building.x + tempNodes.Building.width + 80
      : secondaryStartX;
    let botX = Math.max(secondaryStartX, buildingEnd);
    ["EVCS", "ExcessEnergy"].forEach((id) => {
      if (tempNodes[id]?.value > 0) {
        initialPositions[id] = { x: botX, y: Y_BOTTOM };
        botX += tempNodes[id].width + 50;
      }
    });

    setScale(calculatedScale);
    setBaseNodes(tempNodes);
    setBaseLinks(tempLinks);
    initialNodePositionsRef.current = initialPositions;
    setNodePositions(initialPositions);
    setIsReady(true);
  }, [flowInput]);

  const { renderNodes, renderLinks, buildingStats, nodeBreakdowns, maxRightEdge, maxBottomEdge } =
    useMemo(() => {
    if (!isReady) {
      return {
        renderNodes: {},
        renderLinks: [],
        buildingStats: {},
        nodeBreakdowns: {},
        maxRightEdge: CANVAS_WIDTH,
        maxBottomEdge: CANVAS_HEIGHT,
      };
    }

    const computedNodes = {};
    let currentMaxRight = CANVAS_WIDTH;
    let currentMaxBottom = CANVAS_HEIGHT;

    Object.keys(baseNodes).forEach((id) => {
      if (baseNodes[id].value > 0) {
        const xPos = nodePositions[id]?.x || 0;
        const yPos = nodePositions[id]?.y || 0;

        computedNodes[id] = {
          ...baseNodes[id],
          x: xPos,
          y: yPos,
          overlays: [],
          sourceOverlays: [],
        };

        const rightEdge = xPos + baseNodes[id].width + 100;
        if (rightEdge > currentMaxRight) currentMaxRight = rightEdge;
        const bottomEdge = yPos + 180;
        if (bottomEdge > currentMaxBottom) currentMaxBottom = bottomEdge;
      }
    });

    const linksWithData = baseLinks
      .map((link, idx) => ({
        ...link,
        id: `link-${idx}`,
        sNode: computedNodes[link.source],
        tNode: computedNodes[link.target],
      }))
      .filter((l) => l.sNode && l.tNode);

    const inboundByNode = {};
    const outboundByNode = {};
    Object.keys(computedNodes).forEach((id) => {
      inboundByNode[id] = [];
      outboundByNode[id] = [];
    });

    linksWithData.forEach((l) => {
      outboundByNode[l.source]?.push({ label: l.tNode.label, value: l.value });
      inboundByNode[l.target]?.push({ label: l.sNode.label, value: l.value });
    });

    let utilityToBuilding = 0;
    let selfToBuilding = 0;
    linksWithData.forEach((l) => {
      if (l.tNode.id === "Building") {
        if (l.sNode.id === "Grid") utilityToBuilding += l.value;
        else selfToBuilding += l.value;
      }
    });

    const totalBuilding = utilityToBuilding + selfToBuilding;
    const utilityPct = totalBuilding ? ((utilityToBuilding / totalBuilding) * 100).toFixed(2) : 0;
    const selfPct = totalBuilding ? ((selfToBuilding / totalBuilding) * 100).toFixed(2) : 0;

    Object.keys(computedNodes).forEach((nodeId) => {
      const n = computedNodes[nodeId];
      const outLinks = linksWithData.filter((l) => l.source === nodeId);
      outLinks.sort((a, b) => {
        if (nodeId === "PV") {
          const order = { Battery: 1, Building: 2, GridSell: 3, ExcessEnergy: 4, EVCS: 5 };
          const aOrder = order[a.tNode.id] || 99;
          const bOrder = order[b.tNode.id] || 99;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        if (nodeId === "Genset") {
          const order = { GridSell: 1, Battery: 2, Building: 3, ExcessEnergy: 4, EVCS: 5 };
          const aOrder = order[a.tNode.id] || 99;
          const bOrder = order[b.tNode.id] || 99;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        return a.tNode.x - b.tNode.x;
      });
      let currentX = 0;
      outLinks.forEach((l) => {
        l.sourceOffsetX = currentX;
        currentX += l.value * scale * n.widthScale;
      });
    });

    Object.keys(computedNodes).forEach((nodeId) => {
      const n = computedNodes[nodeId];
      const inLinks = linksWithData.filter((l) => l.target === nodeId);
      inLinks.sort((a, b) => {
        if (nodeId === "Building") {
          const order = { Grid: 1, Battery: 2, PV: 3, Genset: 4 };
          const aOrder = order[a.sNode.id] || 99;
          const bOrder = order[b.sNode.id] || 99;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        if (nodeId === "Battery") {
          const order = { Grid: 1, PV: 2, Genset: 3 };
          const aOrder = order[a.sNode.id] || 99;
          const bOrder = order[b.sNode.id] || 99;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        if (nodeId === "GridSell") {
          const order = { Battery: 1, PV: 2, Genset: 3 };
          const aOrder = order[a.sNode.id] || 99;
          const bOrder = order[b.sNode.id] || 99;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        return a.sNode.x - b.sNode.x;
      });
      let currentX = 0;
      inLinks.forEach((l) => {
        l.targetOffsetX = currentX;
        currentX += l.value * scale * n.widthScale;
      });
    });

    const computedLinks = [];
    linksWithData.forEach((l) => {
      const s = l.sNode;
      const t = l.tNode;
      const w = l.value * scale;

      const outBaseOffset = (s.width - s.outValue * scale * s.widthScale) / 2;
      const inBaseOffset = (t.width - t.inValue * scale * t.widthScale) / 2;
      const startX = s.x + outBaseOffset + l.sourceOffsetX;
      const endX = t.x + inBaseOffset + l.targetOffsetX;

      const isSameTier = Math.abs(t.y - s.y) < 20;
      const isUpflow = s.y > t.y + 20;
      const dx = Math.abs(endX - startX);
      const dy = Math.abs(t.y - s.y);

      let startY;
      let endY;
      let cp1Y;
      let cp2Y;

      if (isSameTier) {
        const curvature = Math.max(60, dx * 0.4);
        startY = s.y + NODE_HEIGHT;
        endY = t.y + NODE_HEIGHT;
        cp1Y = startY + curvature;
        cp2Y = endY + curvature;
      } else {
        let curvature = dy * 0.45;
        if (dy < 50) {
          curvature = Math.max(curvature, dx * 0.3);
        } else {
          curvature += dx * 0.2;
          curvature = Math.min(curvature, dy * 0.8);
        }

        startY = isUpflow ? s.y : s.y + NODE_HEIGHT;
        endY = isUpflow ? t.y + NODE_HEIGHT : t.y;
        const yDir = isUpflow ? -1 : 1;
        cp1Y = startY + curvature * yDir;
        cp2Y = endY - curvature * yDir;
      }

      const curveLowestPoint = Math.max(startY, endY, cp1Y, cp2Y);
      if (curveLowestPoint + 50 > currentMaxBottom) {
        currentMaxBottom = curveLowestPoint + 50;
      }

      const pathD = `
        M ${startX} ${startY}
        L ${startX + w} ${startY}
        C ${startX + w} ${cp1Y},
          ${endX + w} ${cp2Y},
          ${endX + w} ${endY}
        L ${endX} ${endY}
        C ${endX} ${cp2Y},
          ${startX} ${cp1Y},
          ${startX} ${startY}
        Z
      `;

      const effectiveLinkColor =
        t.id === "ExcessEnergy"
          ? baseNodesConfig.ExcessEnergy.color
          : s.id === "GridSell" || t.id === "GridSell"
          ? baseNodesConfig.GridSell.color
          : s.color;

      computedLinks.push({
        id: l.id,
        pathD,
        color: effectiveLinkColor,
        value: l.value,
        sourceId: s.id,
        targetId: t.id,
        sourceLabel: s.label,
        targetLabel: t.label,
      });
      t.overlays.push({ x: endX, width: w, color: effectiveLinkColor });
      if (t.id === "GridSell") {
        s.sourceOverlays.push({ x: startX, width: w, color: baseNodesConfig.GridSell.color });
      }
      if (t.id === "ExcessEnergy") {
        s.sourceOverlays.push({ x: startX, width: w, color: baseNodesConfig.ExcessEnergy.color });
      }
    });

    computedLinks.sort((a, b) => b.value - a.value);

    const computedNodeBreakdowns = {};
    Object.keys(computedNodes).forEach((id) => {
      const inbound = [...(inboundByNode[id] || [])].sort((a, b) => b.value - a.value);
      const outbound = [...(outboundByNode[id] || [])].sort((a, b) => b.value - a.value);
      const inTotal = inbound.reduce((sum, item) => sum + item.value, 0);
      const outTotal = outbound.reduce((sum, item) => sum + item.value, 0);
      computedNodeBreakdowns[id] = { inbound, outbound, inTotal, outTotal };
    });

    return {
      renderNodes: computedNodes,
      renderLinks: computedLinks,
      buildingStats: { utilityToBuilding, selfToBuilding, totalBuilding, utilityPct, selfPct },
      nodeBreakdowns: computedNodeBreakdowns,
      maxRightEdge: currentMaxRight,
      maxBottomEdge: currentMaxBottom,
    };
  }, [isReady, baseNodes, baseLinks, nodePositions, scale]);

  const handleMouseDown = (e, id) => {
    if (!svgRef.current) return;
    const svgRect = svgRef.current.getBoundingClientRect();
    setHoveredNodeId(null);
    setHoveredLinkId(null);
    if (tooltipRef.current) {
      tooltipRef.current.style.display = "none";
    }
    setDragInfo({
      id,
      offsetX: e.clientX - svgRect.left - nodePositions[id].x,
      offsetY: e.clientY - svgRect.top - nodePositions[id].y,
    });
  };

  const handleMouseMove = (e) => {
    if (!dragInfo || !svgRef.current) return;
    const svgRect = svgRef.current.getBoundingClientRect();
    setNodePositions((prev) => ({
      ...prev,
      [dragInfo.id]: {
        x: Math.max(0, e.clientX - svgRect.left - dragInfo.offsetX),
        y: Math.max(0, e.clientY - svgRect.top - dragInfo.offsetY),
      },
    }));
  };

  const handleMouseUp = () => setDragInfo(null);

  const getZoomFactor = () => {
    const seApp = document.getElementsByTagName("se-app")?.[0];
    const zoomValue = seApp ? window.getComputedStyle(seApp).zoom : "";
    if (!zoomValue) return 1;
    if (String(zoomValue).includes("%")) {
      const parsedPercent = parseFloat(String(zoomValue));
      return Number.isFinite(parsedPercent) && parsedPercent > 0 ? parsedPercent / 100 : 1;
    }
    const parsed = parseFloat(String(zoomValue));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const setTooltipPosition = (e) => {
    if (!tooltipRef.current) return;
    const zoomFactor = getZoomFactor();
    tooltipRef.current.style.left = `${e.clientX / zoomFactor + 16}px`;
    tooltipRef.current.style.top = `${e.clientY / zoomFactor - 12}px`;
  };

  const setTooltipContent = (title, value, details = "") => {
    if (tooltipTitleRef.current) {
      tooltipTitleRef.current.textContent = title;
    }
    if (tooltipValueRef.current) {
      tooltipValueRef.current.textContent = value;
    }
    if (tooltipDetailsRef.current) {
      tooltipDetailsRef.current.textContent = details;
    }
  };

  const formatMWh = (value) =>
    `${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} MWh`;

  const handlePathMouseEnter = (e, sourceLabel, targetLabel, val) => {
    if (dragInfo) return;
    if (tooltipRef.current) {
      setTooltipContent(`${sourceLabel} → ${targetLabel}`, formatMWh(val), "");
      tooltipRef.current.style.display = "block";
      setTooltipPosition(e);
    }
  };

  const handleNodeMouseEnter = (e, nodeId) => {
    if (dragInfo) return;
    const node = renderNodes[nodeId];
    const breakdown = nodeBreakdowns[nodeId];
    if (!node || !breakdown || !tooltipRef.current) return;

    setHoveredNodeId(nodeId);
    setHoveredLinkId(null);

    const lines = [];
    if (nodeId === "GridSell") {
      lines.push("Sources:");
      breakdown.inbound.forEach((item) => lines.push(`• ${item.label}: ${formatMWh(item.value)}`));
    } else {
      if (breakdown.inbound.length > 0) {
        lines.push("Received from:");
        breakdown.inbound.forEach((item) => lines.push(`• ${item.label}: ${formatMWh(item.value)}`));
      }
      if (breakdown.outbound.length > 0) {
        if (lines.length > 0) lines.push("");
        lines.push("Sent to:");
        breakdown.outbound.forEach((item) => lines.push(`• ${item.label}: ${formatMWh(item.value)}`));
      }
    }

    setTooltipContent(node.label, formatMWh(node.value), lines.join("\n"));
    tooltipRef.current.style.display = "block";
    setTooltipPosition(e);
  };

  const handleIconMouseEnter = (e, nodeId) => {
    if (dragInfo) return;
    const node = renderNodes[nodeId];
    const breakdown = nodeBreakdowns[nodeId];
    if (!node || !tooltipRef.current) return;

    setHoveredNodeId(nodeId);
    setHoveredLinkId(null);

    const inbound = breakdown?.inbound || [];
    const outbound = breakdown?.outbound || [];
    const lines = [];

    lines.push("Incoming:");
    if (nodeId === "Grid") {
      lines.push(`• Utility: ${formatMWh(node.value)}`);
    } else if (inbound.length > 0) {
      inbound.forEach((item) => lines.push(`• ${item.label}: ${formatMWh(item.value)}`));
    } else {
      lines.push("• None");
    }

    lines.push("");
    lines.push("Outgoing:");
    if (outbound.length > 0) {
      outbound.forEach((item) => lines.push(`• ${item.label}: ${formatMWh(item.value)}`));
    } else {
      lines.push("• None");
    }

    setTooltipContent(node.label, formatMWh(node.value), lines.join("\n"));
    tooltipRef.current.style.display = "block";
    setTooltipPosition(e);
  };

  const handlePathMouseMove = (e) => {
    if (dragInfo) return;
    setTooltipPosition(e);
  };

  const handlePathMouseLeave = () => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = "none";
    }
    setHoveredNodeId(null);
    setHoveredLinkId(null);
  };

  const hoveredConnectedNodeIds = useMemo(() => {
    if (!hoveredNodeId) return null;
    const connected = new Set([hoveredNodeId]);
    renderLinks.forEach((link) => {
      if (link.sourceId === hoveredNodeId) connected.add(link.targetId);
      if (link.targetId === hoveredNodeId) connected.add(link.sourceId);
    });
    return connected;
  }, [hoveredNodeId, renderLinks]);

  const hoveredLinkNodeIds = useMemo(() => {
    if (!hoveredLinkId) return null;
    const activeLink = renderLinks.find((link) => link.id === hoveredLinkId);
    if (!activeLink) return null;
    return new Set([activeLink.sourceId, activeLink.targetId]);
  }, [hoveredLinkId, renderLinks]);

  const getNodeOpacity = (nodeId) => {
    if (dragInfo) return 1;
    if (hoveredConnectedNodeIds) {
      return hoveredConnectedNodeIds.has(nodeId) ? 1 : 0.22;
    }
    if (hoveredLinkNodeIds) {
      return hoveredLinkNodeIds.has(nodeId) ? 1 : 0.22;
    }
    return 1;
  };

  const handleResetLayout = () => {
    setNodePositions(initialNodePositionsRef.current || {});
    setDragInfo(null);
    setHoveredNodeId(null);
    setHoveredLinkId(null);
    if (tooltipRef.current) {
      tooltipRef.current.style.display = "none";
    }
  };

  const handleDownloadSankey = () => {
    if (!svgRef.current) return;
    const serializer = new XMLSerializer();
    const svgMarkup = serializer.serializeToString(svgRef.current);
    const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "microgrid-sankey.svg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isReady) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: pageBg, color: baseTextColor }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center p-5"
      style={{  color: baseTextColor }}
    >
      <div
        className="w-full"
        // style={{ background: cardBg }}
      >
        <div className="flex justify-center">
        {(() => {
          const svgWidth = Math.max(CANVAS_WIDTH, maxRightEdge);
          const svgHeight = Math.max(CANVAS_HEIGHT, maxBottomEdge);
          const viewboxShiftX = 25;

          return (
        <div style={{ width: "100%", maxWidth: "100%", position: "relative", margin: "0 auto", background: pageBg, borderRadius: 12, padding: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 6,
            whiteSpace: "nowrap",
          }}
        >
          <button
            type="button"
            onClick={handleDownloadSankey}
            title="Download Sankey"
            style={{
              border: "1px solid rgba(128,128,128,0.45)",
              borderRadius: 6,
              background: isDarkMode ? "rgba(20,20,20,0.9)" : "rgba(255,255,255,0.75)",
              color: isDarkMode ? "#e6e6e6" : "#2f2f2f",
              cursor: "pointer",
              height: 32,
              minWidth: 82,
              lineHeight: "30px",
              textAlign: "center",
              fontSize: 13,
              padding: "0 10px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Download
          </button>
          <button
            type="button"
            onClick={handleResetLayout}
            title="Reset Sankey position"
            style={{
              border: "1px solid rgba(128,128,128,0.45)",
              borderRadius: 6,
              background: isDarkMode ? "rgba(20,20,20,0.9)" : "rgba(255,255,255,0.75)",
              color: isDarkMode ? "#e6e6e6" : "#2f2f2f",
              cursor: "pointer",
              width: 32,
              height: 32,
              lineHeight: "30px",
              textAlign: "center",
              fontSize: 18,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ↺
          </button>
        </div>
        <svg
          ref={svgRef}
          width={svgWidth}
          height={svgHeight}
          viewBox={`${-viewboxShiftX} 0 ${svgWidth + viewboxShiftX} ${svgHeight}`}
          preserveAspectRatio="xMidYMid meet"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ display: "block", margin: "0 auto", cursor: dragInfo ? "grabbing" : "default", width: "100%", maxWidth: "100%", height: "auto" }}
        >
          {renderLinks.map((link) => (
            <path
              key={link.id}
              d={link.pathD}
              fill={link.color}
              style={{
                opacity:
                  dragInfo
                    ? 0.85
                    :
                  hoveredNodeId && (link.sourceId === hoveredNodeId || link.targetId === hoveredNodeId)
                    ? 1
                    : hoveredNodeId
                    ? 0.22
                    : hoveredLinkId === link.id
                    ? 1
                    : hoveredLinkId
                    ? 0.22
                    : 0.85,
                cursor: dragInfo ? "grabbing" : "pointer",
                transition: "opacity 140ms ease",
              }}
              onMouseEnter={(e) =>
                (() => {
                  setHoveredLinkId(link.id);
                  setHoveredNodeId(null);
                  handlePathMouseEnter(e, link.sourceLabel, link.targetLabel, link.value);
                })()
              }
              onMouseMove={handlePathMouseMove}
              onMouseLeave={handlePathMouseLeave}
            />
          ))}

          {Object.values(renderNodes).map((n) => {
            const isGrabbing = dragInfo?.id === n.id;
            return (
              <g
                key={n.id}
                onMouseDown={(e) => handleMouseDown(e, n.id)}
                className={isGrabbing ? "cursor-grabbing" : "cursor-grab"}
                style={{ opacity: getNodeOpacity(n.id), transition: "opacity 140ms ease" }}
              >
                {(n.id === "Grid" || n.id === "GridSell") && (
                  <g
                    onMouseEnter={n.id === "GridSell" ? (e) => handleNodeMouseEnter(e, "GridSell") : undefined}
                    onMouseMove={n.id === "GridSell" ? handlePathMouseMove : undefined}
                    onMouseLeave={n.id === "GridSell" ? handlePathMouseLeave : undefined}
                  >
                    <rect x={n.x} y={n.y - 70} width={n.width} height={70} fill={panelFill} rx="2" />
                    <text
                      x={n.x + 10}
                      y={n.y - 50}
                      fill={n.id === "Grid" ? "#62b5e5" : baseNodesConfig.GridSell.color}
                      fontSize="13"
                    >
                      {n.id === "Grid" ? "Buying" : "Selling"}
                    </text>
                    <text x={n.x + 10} y={n.y - 25} fill={baseTextColor} fontSize="22" fontWeight="bold">
                      {n.value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} MWh
                    </text>
                    <text x={n.x + 10} y={n.y - 10} fill={panelSubText} fontSize="12">
                      {n.id === "Grid" ? "from utility" : "to utility"}
                    </text>
                  </g>
                )}

                {(n.id === "PV" || n.id === "Battery" || n.id === "Genset") && (
                  <g
                    style={{ cursor: dragInfo ? "grabbing" : "pointer" }}
                    onMouseEnter={(e) => handleIconMouseEnter(e, n.id)}
                    onMouseMove={handlePathMouseMove}
                    onMouseLeave={handlePathMouseLeave}
                  >
                    <circle
                      cx={n.x + n.width / 2}
                      cy={n.y - 45}
                      r={ICON_HIT_RADIUS}
                      fill="transparent"
                    />
                    <circle
                      cx={n.x + n.width / 2}
                      cy={n.y - 45}
                      r={ICON_RING_RADIUS}
                      fill="none"
                      stroke={n.color}
                      strokeWidth={hoveredNodeId === n.id ? "3" : "2.2"}
                      style={{ transition: "stroke-width 140ms ease" }}
                    />
                    {(() => {
                      const IconComponent = NODE_ICON_MAP[n.id];
                      if (!IconComponent) return null;
                      return (
                        <g
                          transform={`translate(${n.x + n.width / 2 - ICON_SIZE / 2}, ${
                            n.y - 45 - ICON_SIZE / 2
                          })`}
                          style={{ pointerEvents: "none" }}
                        >
                          <IconComponent
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                            fill={n.color}
                            stroke={n.color}
                          />
                        </g>
                      );
                    })()}
                    <text
                      x={n.x + n.width / 2}
                      y={n.y - 12}
                      fill={n.color}
                      fontSize="14"
                      fontWeight="600"
                      textAnchor="middle"
                    >
                      {n.value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} MWh
                    </text>
                  </g>
                )}

                {n.id !== "Building" && (
                  <rect
                    x={n.x}
                    y={n.y}
                    width={n.width}
                    height={NODE_HEIGHT}
                    fill={
                      n.id === "GridSell"
                        ? baseNodesConfig.GridSell.color
                        : n.id === "ExcessEnergy"
                        ? baseNodesConfig.ExcessEnergy.color
                        : n.color
                    }
                    rx="1"
                    style={n.id === "GridSell" ? { cursor: "pointer" } : undefined}
                    onMouseEnter={n.id === "GridSell" ? (e) => handleNodeMouseEnter(e, "GridSell") : undefined}
                    onMouseMove={n.id === "GridSell" ? handlePathMouseMove : undefined}
                    onMouseLeave={n.id === "GridSell" ? handlePathMouseLeave : undefined}
                  />
                )}

                {n.sourceOverlays?.length > 0 && (
                  <g>
                    {n.sourceOverlays.map((overlay, idx) => (
                      <g key={`source-overlay-${idx}`}>
                        <rect
                          x={overlay.x}
                          y={n.y}
                          width={overlay.width + 0.5}
                          height={NODE_HEIGHT}
                          fill={overlay.color}
                        />
                      </g>
                    ))}
                  </g>
                )}

                {n.id === "Grid" && (
                  <g
                    style={{ cursor: dragInfo ? "grabbing" : "pointer" }}
                    onMouseEnter={(e) => handleIconMouseEnter(e, "Grid")}
                    onMouseMove={handlePathMouseMove}
                    onMouseLeave={handlePathMouseLeave}
                  >
                    <circle
                      cx={n.x - GRID_ICON_CENTER_OFFSET_X}
                      cy={n.y + NODE_HEIGHT / 2}
                      r={ICON_HIT_RADIUS}
                      fill="transparent"
                    />
                    <circle
                      cx={n.x - GRID_ICON_CENTER_OFFSET_X}
                      cy={n.y + NODE_HEIGHT / 2}
                      r={ICON_RING_RADIUS}
                      fill="none"
                      stroke="#62b5e5"
                      strokeWidth={hoveredNodeId === "Grid" ? "3" : "2.2"}
                      style={{ transition: "stroke-width 140ms ease" }}
                    />
                    <g
                      transform={`translate(${n.x - GRID_ICON_CENTER_OFFSET_X - ICON_SIZE / 2}, ${
                        n.y + NODE_HEIGHT / 2 - ICON_SIZE / 2
                      })`}
                    >
                      <GridIcon width={ICON_SIZE} height={ICON_SIZE} fill="#62b5e5" stroke="#62b5e5" />
                    </g>
                  </g>
                )}

                {n.overlays.length > 0 && (
                  <g>
                    {n.overlays.map((overlay, idx) => (
                      <g key={`overlay-${idx}`}>
                        <rect
                          x={overlay.x}
                          y={n.y}
                          width={overlay.width + 0.5}
                          height={NODE_HEIGHT}
                          fill={
                            n.id === "GridSell"
                              ? baseNodesConfig.GridSell.color
                              : n.id === "ExcessEnergy"
                              ? baseNodesConfig.ExcessEnergy.color
                              : overlay.color
                          }
                        />
                      </g>
                    ))}
                  </g>
                )}

                {n.id === "Building" && (
                  <g>
                    {(() => {
                      const buildingValueText = `${buildingStats.totalBuilding.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} MWh`;
                      const estimatedValueWidth = buildingValueText.length * BUILDING_VALUE_FONT_SIZE * 0.58;
                      const totalContentWidth =
                        BUILDING_ICON_SIZE + BUILDING_ICON_TEXT_GAP + estimatedValueWidth;
                      const contentStartX = n.x + Math.max(10, (n.width - totalContentWidth) / 2);
                      const buildingIconX = contentStartX;
                      const buildingValueX = buildingIconX + BUILDING_ICON_SIZE + BUILDING_ICON_TEXT_GAP;
                      return (
                        <>
                    {/* <rect x={n.x} y={n.y + NODE_HEIGHT} width={n.width} height={50} fill={buildingPanelFill} /> */}

                    {/* {buildingStats.utilityPct > 0 && (
                      <g>
                        <text
                          x={n.x + 10}
                          y={n.y + NODE_HEIGHT + 22}
                          fill="#62b5e5"
                          fontSize="16"
                          fontWeight="bold"
                        >
                          {buildingStats.utilityToBuilding.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} MWh 
                        </text>
                        <text x={n.x + 10} y={n.y + NODE_HEIGHT + 40} fill={panelSubText} fontSize="11">
                          from utility
                        </text>
                      </g>
                    )}

                    {buildingStats.selfPct > 0 && (
                      <g>
                        <text
                          x={n.x + n.width - 10}
                          y={n.y + NODE_HEIGHT + 22}
                          fill="#6cc24a"
                          fontSize="16"
                          fontWeight="bold"
                          textAnchor="end"
                        >
                          {buildingStats.selfToBuilding.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} MWh 
                        </text>
                        <text
                          x={n.x + n.width - 10}
                          y={n.y + NODE_HEIGHT + 40}
                          fill={panelSubText}
                          fontSize="11"
                          textAnchor="end"
                        >
                          from Self Production
                        </text>
                      </g>
                    )} */}

                    <rect
                      x={n.x}
                      y={n.y + NODE_HEIGHT}
                      width={n.width}
                      height={BUILDING_PANEL_HEIGHT}
                      fill={buildingTotalFill}
                    />
                    <g
                      transform={`translate(${buildingIconX}, ${
                        n.y + NODE_HEIGHT + (BUILDING_PANEL_HEIGHT - BUILDING_ICON_SIZE) / 2
                      })`}
                    >
                      <LoadIcon
                        width={BUILDING_ICON_SIZE}
                        height={BUILDING_ICON_SIZE}
                        fill="#6cc24a"
                        stroke="#6cc24a"
                      />
                    </g>
                    <text
                      x={buildingValueX}
                      y={n.y + NODE_HEIGHT + BUILDING_PANEL_TOP_PADDING + BUILDING_VALUE_FONT_SIZE}
                      fill="#6cc24a"
                      fontSize={BUILDING_VALUE_FONT_SIZE}
                      fontWeight="bold"
                      textAnchor="start"
                    >
                      {buildingValueText}
                    </text>
                    <text
                      x={buildingValueX}
                      y={
                        n.y +
                        NODE_HEIGHT +
                        BUILDING_PANEL_TOP_PADDING +
                        BUILDING_VALUE_FONT_SIZE +
                        BUILDING_TEXT_GAP +
                        BUILDING_LABEL_FONT_SIZE
                      }
                      fill="#6cc24a"
                      fontSize={BUILDING_LABEL_FONT_SIZE}
                      textAnchor="start"
                    >
                      Load
                    </text>

                    <rect
                      x={n.x}
                      y={n.y + NODE_HEIGHT + BUILDING_PANEL_HEIGHT}
                      width={n.width}
                      height={12}
                      fill="#df8b1b"
                    />
                        </>
                      );
                    })()}
                  </g>
                )}

                {n.id !== "Grid" &&
                  n.id !== "GridSell" &&
                  n.id !== "PV" &&
                  n.id !== "Battery" &&
                  n.id !== "Genset" &&
                  n.id !== "Building" && (
                    <g>
                      <text
                        x={n.x + n.width / 2}
                        y={n.y - 12}
                        fill={baseTextColor}
                        fontSize="14"
                        fontWeight="600"
                        textAnchor="middle"
                        className="select-none"
                      >
                        {n.label}
                      </text>
                      <text
                        x={n.x + n.width / 2}
                        y={n.y + NODE_HEIGHT + 20}
                        fill="#a0a0a0"
                        fontSize="12"
                        textAnchor="middle"
                        className="select-none"
                      >
                        {n.value.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} MWh
                      </text>
                    </g>
                  )}
              </g>
            );
          })}
        </svg>
        </div>
          );
        })()}
        </div>
      </div>

      <div
        ref={tooltipRef}
        style={{
          display: "none",
          position: "fixed",
          left: 0,
          top: 0,
          background: "rgba(0,0,0,0.85)",
          color: "#ffffff",
          padding: "8px 12px",
          borderRadius: 6,
          fontSize: 13,
          pointerEvents: "none",
          zIndex: 9999,
          boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
          whiteSpace: "pre-line",
          maxWidth: 320,
        }}
      >
        <strong ref={tooltipTitleRef} />
        <br />
        <span ref={tooltipValueRef} style={{ color: "#a0a0a0" }} />
        <div ref={tooltipDetailsRef} style={{ marginTop: 6, color: "#d0d0d0", lineHeight: 1.35 }} />
      </div>
    </div>
  );
}
